/**
 * WhySoulumoPage — the deeper "Why Soulumo?" narrative.
 *
 * Mar 5 2026 · Michael's brief: this is the click-through page from
 * the Soulumo landing "Why Soulumo? →" link. It carries the full
 * philosophical proposition of Soulumo (inherited programming vs
 * consciously designed inner architecture) — read slowly, one thought
 * at a time.
 *
 * The narrator audio slot is present but not yet wired to a real file;
 * Michael will record and place the file at:
 *   /soulumo-why-narration.mp3   (public/ folder)
 * When it exists, the player will offer to play as the page opens.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";

const TOK = {
  bg: "radial-gradient(ellipse at 50% 15%, #131f38 0%, #0a132a 42%, #030710 88%)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.78)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

// The narrative — kept as an ordered list of short paragraphs so we can
// stagger reveal them and give each thought its own breathing space.
const PARAGRAPHS_INTRO = [
  "Not everything within you was chosen by you.",
  "From the beginning of your life, you absorbed beliefs, expectations, emotional patterns and ways of seeing the world.",
  "Some came from your parents.",
  "Some came from your culture, education and experiences.",
  "Some were formed during moments you may no longer consciously remember.",
  "Over time, this conditioning became part of your inner architecture — quietly influencing what you believe is possible, how you speak to yourself, how you respond to life and even who you believe yourself to be.",
  "But this programming was inherited and absorbed.",
  "It was not consciously designed by you.",
];

const PARAGRAPHS_TURN = [
  "Soulumo gives you the opportunity to change that.",
];

const PARAGRAPHS_BODY = [
  "Soulumo helps you create deeply personal audio installations designed around the beliefs, emotions and inner language you consciously choose.",
  "You can use your own words and even your own voice — the voice your subconscious recognises most intimately — layered with carefully selected music and frequency to create a calm, receptive listening experience.",
  "This is more than listening to positive statements.",
  "It is an opportunity to begin speaking to yourself at a deeper level — with intention, repetition and language that genuinely belongs to you.",
];

const NARRATION_SRC = "/soulumo-why-narration.mp3";

export default function WhySoulumoPage() {
  const nav = useNavigate();
  const audioRef = useRef(null);
  const [audioReady, setAudioReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Detect whether Michael's recording has been placed yet. We do a
  // silent HEAD probe — but because the SPA serves index.html for any
  // unknown path (returns 200), we must also verify the response is an
  // audio file. Only then do we show the player.
  useEffect(() => {
    let cancelled = false;
    fetch(NARRATION_SRC, { method: "HEAD" })
      .then((r) => {
        if (cancelled) return;
        const ct = r.headers.get("content-type") || "";
        if (r.ok && ct.startsWith("audio/")) setAudioReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="soulumo-why-page"
    >
      {/* Soft breathing halo */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-12%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 560,
          height: 560,
          background:
            "radial-gradient(circle, rgba(224,169,122,0.22) 0%, rgba(224,169,122,0.06) 32%, transparent 66%)",
          filter: "blur(30px)",
          animation: reduceMotion ? undefined : "why-breathe 12s ease-in-out infinite",
        }}
        aria-hidden
      />
      <style>{`
        @keyframes why-breathe {
          0%, 100% { opacity: 0.85; transform: translateX(-50%) scale(1); }
          50%      { opacity: 1;    transform: translateX(-50%) scale(1.06); }
        }
        @keyframes why-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .why-p {
          animation: ${reduceMotion ? "none" : "why-rise 0.9s ease-out both"};
        }
      `}</style>

      {/* Top-bar — shared SoulumoHeader (Mar 6 2026) */}
      <SoulumoHeader />

      {/* Header */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 pt-14 pb-10 text-center">
        <div
          className="text-[0.56rem] uppercase tracking-[0.4em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          · Why Soulumo ·
        </div>
        <h1
          className="mt-5 font-light leading-tight"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(2rem, 6vw, 3.1rem)",
            color: TOK.cream,
            letterSpacing: "-0.01em",
          }}
          data-testid="soulumo-why-title"
        >
          Become the architect of your inner world.
        </h1>

        {/* Audio player — only visible once narration file is placed */}
        {audioReady && (
          <div
            className="mt-8 rounded-2xl px-5 py-4 mx-auto max-w-md flex items-center gap-4"
            style={{
              background: "rgba(224,169,122,0.06)",
              border: `1px solid ${TOK.goldMist}`,
            }}
          >
            <button
              onClick={togglePlay}
              data-testid="soulumo-why-audio-toggle"
              className="rounded-full flex-shrink-0"
              style={{
                width: 44,
                height: 44,
                background: TOK.gold,
                color: "#0a132a",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "1.1rem",
              }}
              aria-label={playing ? "Pause narration" : "Play narration"}
            >
              {playing ? "❚❚" : "▶"}
            </button>
            <div className="text-left flex-1">
              <div
                className="text-[0.56rem] uppercase tracking-[0.32em]"
                style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
              >
                Read by Michael
              </div>
              <div
                className="italic"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  color: TOK.creamDim,
                  fontSize: "0.95rem",
                }}
              >
                Play while you read below.
              </div>
            </div>
            <audio
              ref={audioRef}
              src={NARRATION_SRC}
              preload="metadata"
              onEnded={() => setPlaying(false)}
              onPause={() => setPlaying(false)}
              onPlay={() => setPlaying(true)}
            />
          </div>
        )}
      </section>

      {/* Narrative — three staged blocks */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 pb-6">
        {PARAGRAPHS_INTRO.map((p, i) => (
          <p
            key={`intro-${i}`}
            className="why-p mb-5 leading-relaxed"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.08rem, 2.6vw, 1.28rem)",
              color: TOK.creamDim,
              animationDelay: `${i * 0.08}s`,
            }}
          >
            {p}
          </p>
        ))}
      </section>

      {/* The turning line — pulled forward, gold, italic */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 py-6">
        {PARAGRAPHS_TURN.map((p, i) => (
          <p
            key={`turn-${i}`}
            className="why-p italic text-center"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.3rem, 3.5vw, 1.7rem)",
              color: TOK.gold,
              lineHeight: 1.35,
            }}
          >
            {p}
          </p>
        ))}
      </section>

      {/* Second act */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 pt-2 pb-8 text-center">
        <div
          className="mb-6 text-[0.56rem] uppercase tracking-[0.4em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          · Become the architect ·
        </div>
        {PARAGRAPHS_BODY.map((p, i) => (
          <p
            key={`body-${i}`}
            className="why-p mb-5 leading-relaxed text-left"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.08rem, 2.6vw, 1.28rem)",
              color: TOK.creamDim,
            }}
          >
            {p}
          </p>
        ))}
      </section>

      {/* Signature line — the three-part promise */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 py-10 text-center">
        <p
          className="italic font-light"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.6rem, 4.5vw, 2.2rem)",
            color: TOK.cream,
            lineHeight: 1.3,
            letterSpacing: "0.01em",
          }}
          data-testid="soulumo-why-signature"
        >
          Your words. <span style={{ color: TOK.gold }}>Your voice.</span> Your design.
        </p>
      </section>

      {/* Closing */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 pb-16 text-center">
        <p
          className="leading-relaxed"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.1rem, 2.8vw, 1.3rem)",
            color: TOK.creamDim,
          }}
        >
          Because when you begin to change the architecture of your inner world,
          you begin to change the way you experience the outer one.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => nav("/soulumo/join")}
            data-testid="soulumo-why-begin"
            className="rounded-full px-8 py-3"
            style={{
              background: "rgba(224,169,122,0.22)",
              border: `1px solid ${TOK.gold}`,
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.75rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            · Begin your first installation ·
          </button>
          <button
            onClick={() => nav("/soulumo")}
            data-testid="soulumo-why-return"
            className="text-[0.62rem] uppercase tracking-[0.28em]"
            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
          >
            Return to Soulumo
          </button>
        </div>
      </section>

      {/* Footer — shared SoulumoFooter (Mar 6 2026) */}
      <SoulumoFooter />
    </div>
  );
}
